import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService.js';
import toast from 'react-hot-toast';
import usePiBrowserDetection from '../hooks/usePiBrowserDetection.js';

/**
 * Authentication Context
 * Provides authentication state and methods to all components
 */
const AuthContext = createContext({});

/**
 * Custom hook to use authentication context
 * @returns {object} Authentication context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Add this before the AuthProvider component
const validateAuthState = (userData, profileStatus) => {
  //console.log('🔍 Validating auth state:', { userData, profileStatus });

  if (!userData) {
    console.warn('⚠️ Invalid auth state: No user data');
    return false;
  }

  // Required user fields
  const requiredFields = ['id', 'google_id', 'google_email'];
  const missingFields = requiredFields.filter(field => !userData[field]);

  if (missingFields.length > 0) {
    console.warn('⚠️ Invalid auth state: Missing required fields:', missingFields);
    return false;
  }

  // Validate profileStatus
  if (!profileStatus) {
    console.warn('⚠️ Invalid auth state: Missing profile status');
    return false;
  }

  //console.log('✅ Auth state validation passed');
  return true;
};

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to children
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);

  // Pi Browser detection for environment-aware features
  const { isPiBrowser, isDetecting: isPiBrowserDetecting, detectionMethod } = usePiBrowserDetection();

  /**
   * Initialize authentication state on app load
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        //console.log('🔍 Initializing auth context...');
        const isAuthenticated = authService.isAuthenticated();
        const userData = authService.getUserData();


        if (isAuthenticated && userData) {
          try {
            // Verify session with backend
            const currentUser = await authService.getCurrentUser();

            const sessionUser = currentUser?.data?.user || currentUser?.user;
            const sessionStatus = currentUser?.data?.profileStatus || currentUser?.profileStatus;

            if (!sessionUser) {
              console.warn('⚠️ Session verification returned valid response but no user data:', currentUser);
              throw new Error('Invalid session data structure');
            }

            setUser(sessionUser);
            setProfileStatus(sessionStatus);
            setIsAuthenticated(true);
            // console.log('✅ Auth context initialized with valid session');
          } catch (error) {
            console.error('❌ Session verification failed:', error);
            authService.clearTokens();
            setUser(null);
            setIsAuthenticated(false);
            setProfileStatus(null);
          }
        } else {
          //console.log('ℹ️ No valid session found');
          setUser(null);
          setIsAuthenticated(false);
          setProfileStatus(null);
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login with Google Identity Service
   * @returns {Promise<object>} Login result
   */
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      //console.log('🔄 Starting Google authentication flow...');

      if (!authService.isGoogleSDKAvailable()) {
        throw new Error('Google Identity Services not available');
      }

      const result = await authService.loginWithGoogle();


      if (!result.success || !result.data?.user) {
        throw new Error('Authentication response invalid');
      }

      const userData = result.data.user;
      const profileStatus = result.data.profileStatus;

      // Validate authentication state
      if (!validateAuthState(userData, profileStatus)) {
        throw new Error('Invalid authentication state');
      }

      // Important: Set state in this specific order
      setUser(userData);
      setProfileStatus(profileStatus);
      setIsAuthenticated(true);


      // Show success message
      const displayName = userData?.google_name || userData?.full_name || 'User';
      toast.success(`Welcome, ${displayName}!`);

      // Force a state refresh from backend
      await refreshUserData();

      return {
        success: true,
        user: userData,
        profileStatus,
        message: `Welcome, ${displayName}!`
      };
    } catch (error) {
      console.error('❌ Google login error:', error);

      // Clear everything on error
      setUser(null);
      setIsAuthenticated(false);
      setProfileStatus(null);
      authService.clearTokens();

      toast.error(error.message || 'Authentication failed');

      return {
        success: false,
        error: error.message || 'Authentication failed'
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login with Pi Network
   * @returns {Promise<object>} Login result
   */
  const loginWithPi = async () => {
    try {
      setLoading(true);

      // Check if Pi SDK is available
      if (!authService.isPiSDKAvailable()) {
        throw new Error('Pi SDK is not available. Please make sure you are using Pi Browser or refresh the page.');
      }

      // Authenticate with Pi Network
      const result = await authService.loginWithPi();

      if (result.success) {
        setUser(result.user);
        setProfileStatus(result.profileStatus);
        setIsAuthenticated(true);

        // Show personalized success message with Pi username
        const displayName = result.user?.pi_username || 'User';
        const welcomeMessage = `Hello, ${displayName}!`;
        toast.success(welcomeMessage);

        // Handle any stored incomplete payments after successful login
        setTimeout(() => {
          authService.handleStoredIncompletePayment();
        }, 1000);

        return {
          success: true,
          user: result.user,
          profileStatus: result.profileStatus,
          message: welcomeMessage
        };
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('Login error:', error);

      // Show error message
      toast.error(error.message || 'Failed to authenticate with Pi Network');

      // Clear any partial auth state
      setUser(null);
      setIsAuthenticated(false);
      setProfileStatus(null);
      authService.clearTokens();

      return {
        success: false,
        error: error.message || 'Authentication failed'
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      setLoading(true);

      // Call backend logout
      await authService.logout();

      // Clear local state
      setUser(null);
      setIsAuthenticated(false);
      setProfileStatus(null);

      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if backend call fails
      setUser(null);
      setIsAuthenticated(false);
      setProfileStatus(null);
      authService.clearTokens();

      toast.error('Logout completed with errors');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh user data from backend
   */
  const refreshUserData = async () => {
    try {
      const response = await authService.getCurrentUser();

      // authService.getCurrentUser() returns the unwrapped data directly
      // The response structure is just { user, profileStatus }
      const userData = response?.user || response?.data?.user || response?.data?.data?.user;
      const profileStatusData = response?.profileStatus || response?.data?.profileStatus || response?.data?.data?.profileStatus;

      if (!userData) {
        throw new Error('Invalid response format from backend');
      }

      // Update AuthContext state
      setUser(userData);
      setProfileStatus(profileStatusData);
      setIsAuthenticated(true);

      return { user: userData, profileStatus: profileStatusData };
    } catch (error) {
      console.error('Failed to refresh auth state:', error);

      // If refresh fails due to invalid token, logout user
      if (error.response?.status === 401) {
        await logout();
      }

      throw error;
    }
  };

  /**
   * Check if user has specific capability
   * @param {string} capability - Capability to check
   * @returns {boolean} Whether user has the capability
   */
  const hasCapability = (capability) => {
    if (!user?.capabilities) return false;

    const capabilityMap = {
      'donate': user.capabilities.can_donate,
      'create_project': user.capabilities.can_create_project,
      'approve_project': user.capabilities.can_approve_project,
      'manage_admins': user.capabilities.can_manage_admins
    };

    return capabilityMap[capability] || false;
  };

  /**
   * Check if user profile is complete
   * @returns {boolean} Whether profile is complete
   */
  const isProfileComplete = () => {
    return profileStatus?.isComplete || false;
  };

  /**
   * Get profile completion percentage
   * @returns {number} Profile completion percentage
   */
  const getProfileCompletion = () => {
    return profileStatus?.completion || 0;
  };

  /**
   * Get missing profile fields
   * @returns {array} Array of missing field names
   */
  const getMissingProfileFields = () => {
    return profileStatus?.missingFields || [];
  };

  /**
   * Context value object
   */
  const contextValue = {
    // State
    user,
    isAuthenticated,
    loading,
    profileStatus,

    // Pi Browser detection
    isPiBrowser,
    isPiBrowserDetecting,
    detectionMethod,

    // Methods
    loginWithPi,
    loginWithGoogle,
    logout,
    refreshUserData,

    // Utility methods
    hasCapability,
    isProfileComplete,
    getProfileCompletion,
    getMissingProfileFields,

    // Service access (for advanced use cases)
    authService
  };


  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
